import React, { useState } from 'react';
import { 
  BarChart, Bar, PieChart, Pie, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart, Area, Scatter
} from 'recharts';

const IPEAnalysisDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // IPE Type Distribution Data
  const ipeTypeData = [
    { name: 'VP', value: 452, percentage: 27.1 },
    { name: 'AdvP', value: 250, percentage: 15.0 },
    { name: 'PV', value: 233, percentage: 14.0 },
    { name: 'DP', value: 207, percentage: 12.4 },
    { name: 'PP', value: 173, percentage: 10.4 },
    { name: 'ConjP', value: 91, percentage: 5.5 },
    { name: 'ModVP', value: 88, percentage: 5.3 },
    { name: 'EP', value: 64, percentage: 3.8 },
    { name: 'PronP', value: 63, percentage: 3.8 },
    { name: 'SCP', value: 20, percentage: 1.2 },
    { name: 'AdjP', value: 11, percentage: 0.7 },
    { name: 'NP', value: 11, percentage: 0.7 },
    { name: 'CCP', value: 3, percentage: 0.2 },
    { name: 'IntjP', value: 1, percentage: 0.1 }
  ];

  // Top 15 IPE Expressions Data
  const topExpressionsData = [
    { name: 'going to', value: 148 },
    { name: 'a lot', value: 129 },
    { name: 'there are', value: 67 },
    { name: 'there is', value: 61 },
    { name: 'such as', value: 58 },
    { name: 'have to', value: 54 },
    { name: 'used to', value: 39 },
    { name: 'of course', value: 29 },
    { name: 'each other', value: 25 },
    { name: 'had to', value: 24 },
    { name: 'due to', value: 23 },
    { name: 'no one', value: 21 },
    { name: 'as well as', value: 20 },
    { name: 'not only', value: 19 },
    { name: 'focus on', value: 18 }
  ];

  // Frequency Band Distribution Data
  const frequencyBandData = [
    { name: 'Band 1', value: 903, percentage: 49.8 },
    { name: 'Band 2', value: 481, percentage: 26.5 },
    { name: 'Band 3', value: 309, percentage: 17.0 },
    { name: 'Band 4', value: 95, percentage: 5.2 },
    { name: 'Band 5', value: 25, percentage: 1.4 }
  ];

  // Group and Phase Analysis Data
  const controlGroupData = [
    { phase: 'SP1', ipes: 174, words: 10331, density: 1.68, variety: 0.47, highFreqRatio: 0.79 },
    { phase: 'SP2', ipes: 246, words: 14450, density: 1.70, variety: 0.39, highFreqRatio: 0.80 },
    { phase: 'W1', ipes: 60, words: 4104, density: 1.46, variety: 0.68, highFreqRatio: 0.78 },
    { phase: 'W2', ipes: 88, words: 8117, density: 1.08, variety: 0.65, highFreqRatio: 0.84 },
    { phase: 'W3', ipes: 108, words: 8023, density: 1.35, variety: 0.58, highFreqRatio: 0.78 }
  ];

  const experimentalGroupData = [
    { phase: 'SP1', ipes: 154, words: 9838, density: 1.57, variety: 0.42, highFreqRatio: 0.84 },
    { phase: 'SP2', ipes: 358, words: 19143, density: 1.87, variety: 0.40, highFreqRatio: 0.73 },
    { phase: 'W1', ipes: 43, words: 3719, density: 1.16, variety: 0.74, highFreqRatio: 0.77 },
    { phase: 'W2', ipes: 282, words: 10650, density: 2.65, variety: 0.47, highFreqRatio: 0.72 },
    { phase: 'W3', ipes: 154, words: 9279, density: 1.66, variety: 0.53, highFreqRatio: 0.70 }
  ];

  const phaseChangesData = [
    { phase: 'Control W1→W2', densityChange: -25.8, ratioChange: 0.07 },
    { phase: 'Control W2→W3', densityChange: 24.2, ratioChange: -0.07 },
    { phase: 'Control SP1→SP2', densityChange: 1.1, ratioChange: 0.01 },
    { phase: 'Exp W1→W2', densityChange: 129.0, ratioChange: -0.05 },
    { phase: 'Exp W2→W3', densityChange: -37.3, ratioChange: -0.02 },
    { phase: 'Exp SP1→SP2', densityChange: 19.5, ratioChange: -0.11 }
  ];

  // Writing vs Speaking Comparison Data
  const modeComparisonData = [
    { 
      mode: 'Writing', 
      ipes: 735, 
      words: 43892, 
      density: 1.67, 
      variety: 0.27, 
      highFreqRatio: 0.75,
      band1: 339,
      band2: 256,
      band3: 150,
      band4: 42,
      band5: 8
    },
    { 
      mode: 'Speaking', 
      ipes: 932, 
      words: 53762, 
      density: 1.73, 
      variety: 0.26, 
      highFreqRatio: 0.78,
      band1: 564,
      band2: 225,
      band3: 159,
      band4: 53,
      band5: 17
    }
  ];

  // Top expressions per mode
  const topExpressionsPerMode = [
    { mode: 'Writing', expression: 'a lot', value: 66 },
    { mode: 'Writing', expression: 'going to', value: 33 },
    { mode: 'Writing', expression: 'there is', value: 24 },
    { mode: 'Writing', expression: 'used to', value: 23 },
    { mode: 'Writing', expression: 'there are', value: 22 },
    { mode: 'Speaking', expression: 'going to', value: 115 },
    { mode: 'Speaking', expression: 'a lot', value: 63 },
    { mode: 'Speaking', expression: 'have to', value: 46 },
    { mode: 'Speaking', expression: 'there are', value: 45 },
    { mode: 'Speaking', expression: 'such as', value: 44 }
  ];

  // CERF Level Comparison Data
  // const cerfA2Data = [
  //   { phase: 'SP1', ipes: 3, words: 269, density: 1.12, highFreqRatio: 1.00 },
  //   { phase: 'SP2', ipes: 5, words: 463, density: 1.08, highFreqRatio: 0.80 },
  //   { phase: 'W1', ipes: 1, words: 116, density: 0.86, highFreqRatio: 1.00 },
  //   { phase: 'W2', ipes: 3, words: 328, density: 0.91, highFreqRatio: 1.00 },
  //   { phase: 'W3', ipes: 3, words: 275, density: 1.09, highFreqRatio: 0.67 }
  // ];

  const cerfB1Data = [
    { phase: 'SP1', ipes: 126, words: 8998,  density: 1.40, highFreqRatio: 0.80 },
    { phase: 'SP2', ipes: 262, words: 13747, density: 1.91, highFreqRatio: 0.83 },
    { phase: 'W1',  ipes: 46,  words: 3445,  density: 1.34, highFreqRatio: 0.74 },
    { phase: 'W2',  ipes: 159, words: 8716,  density: 1.82, highFreqRatio: 0.78 },
    { phase: 'W3',  ipes: 97,  words: 7719,  density: 1.26, highFreqRatio: 0.78 }
  ];

  const cerfB2Data = [
    { phase: 'SP1', ipes: 202, words: 11171, density: 1.81, highFreqRatio: 0.82 },
    { phase: 'SP2', ipes: 342, words: 19846, density: 1.72, highFreqRatio: 0.70 },
    { phase: 'W1', ipes: 57, words: 4378, density: 1.30, highFreqRatio: 0.81 },
    { phase: 'W2', ipes: 211, words: 10051, density: 2.10, highFreqRatio: 0.73 },
    { phase: 'W3', ipes: 165, words: 9583, density: 1.72, highFreqRatio: 0.71 }
  ];

  const b1b2ComparisonData = [
       { phase: 'SP1', b1Density: 1.40, b2Density: 1.81, diffDensity:  0.41, b1HFR: 0.80, b2HFR: 0.82, diffHFR:  0.02 },
       { phase: 'SP2', b1Density: 1.91, b2Density: 1.72, diffDensity: -0.19, b1HFR: 0.83, b2HFR: 0.70, diffHFR: -0.13 },
       { phase: 'W1',  b1Density: 1.34, b2Density: 1.30, diffDensity: -0.04, b1HFR: 0.74, b2HFR: 0.81, diffHFR:  0.07 },
       { phase: 'W2',  b1Density: 1.82, b2Density: 2.10, diffDensity:  0.28, b1HFR: 0.78, b2HFR: 0.73, diffHFR: -0.05 },
       { phase: 'W3',  b1Density: 1.26, b2Density: 1.72, diffDensity:  0.46, b1HFR: 0.78, b2HFR: 0.71, diffHFR: -0.07 }
  ];

  const COLORS = [
    '#8884d8', '#83a6ed', '#8dd1e1', '#82ca9d', '#a4de6c', 
    '#d0ed57', '#ffc658', '#ff8042', '#ff6361', '#bc5090',
    '#58508d', '#003f5c', '#444e86', '#955196', '#dd5182'
  ];

  // PieChart with legend component using inline styles
  const PieChartWithLegend = ({ data, dataKey, nameKey }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={100}
            fill="#8884d8"
            dataKey={dataKey}
            nameKey={nameKey}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value, name) => [`${value}`, name]} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );

  // Common inline style objects
  const containerStyle = {
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    padding: '1.5rem'
  };

  const innerContainerStyle = {
    maxWidth: '80rem',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '2.5rem'
  };

  const titleStyle = {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    color: '#1F2937'
  };

  const subtitleStyle = {
    color: '#4B5563',
    marginTop: '0.5rem'
  };

  const navContainerStyle = {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'center'
  };

  const navBoxStyle = {
    backgroundColor: '#FFFFFF',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)'
  };

  const navStyle = {
    display: 'flex',
    overflowX: 'auto'
  };

  const navButtonBase = {
    padding: '0.75rem 1rem',
    fontWeight: 500,
    fontSize: '0.875rem',
    cursor: 'pointer',
    border: 'none',
    background: 'none'
  };

  const activeNavButtonStyle = {
    ...navButtonBase,
    color: '#2563eb',
    borderBottom: '2px solid #2563eb'
  };

  const inactiveNavButtonStyle = {
    ...navButtonBase,
    color: '#6B7280'
  };

  const gridTwoColumnsStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2rem'
  };

  const cardStyle = {
    backgroundColor: '#FFFFFF',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)'
  };

  const cardTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '1rem'
  };

  const footerStyle = {
    marginTop: '3rem',
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#6B7280'
  };

  return (
    <div style={containerStyle}>
      <div style={innerContainerStyle}>
        <header style={headerStyle}>
          <h1 style={titleStyle}>IPE Usage Analysis Dashboard</h1>
          <p style={subtitleStyle}>Analysis of 1667 IPEs found in 283/300 transcripts</p>
        </header>

        {/* Navigation Tabs */}
        <div style={navContainerStyle}>
          <div style={navBoxStyle}>
            <nav style={navStyle}>
              <button
                onClick={() => setActiveTab('overview')}
                style={activeTab === 'overview' ? activeNavButtonStyle : inactiveNavButtonStyle}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('groups')}
                style={activeTab === 'groups' ? activeNavButtonStyle : inactiveNavButtonStyle}
              >
                Group Analysis
              </button>
              <button
                onClick={() => setActiveTab('modeComparison')}
                style={activeTab === 'modeComparison' ? activeNavButtonStyle : inactiveNavButtonStyle}
              >
                Writing vs Speaking
              </button>
              <button
                onClick={() => setActiveTab('cerfLevels')}
                style={activeTab === 'cerfLevels' ? activeNavButtonStyle : inactiveNavButtonStyle}
              >
                CERF Levels
              </button>
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={gridTwoColumnsStyle}>
            {/* IPE Type Distribution */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>IPE Type Distribution</h2>
              <PieChartWithLegend data={ipeTypeData} dataKey="value" nameKey="name" />
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6B7280', textAlign: 'center' }}>
                Distribution of 1667 IPEs by grammatical type
              </div>
            </div>

            {/* Top 15 IPE Expressions */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>Top 15 IPE Expressions</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={[...topExpressionsData].sort((a, b) => b.value - a.value)}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 70, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={70} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Frequency Band Distribution */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>Frequency Band Distribution</h2>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '50%' }}>
                  <PieChartWithLegend data={frequencyBandData} dataKey="value" nameKey="name" />
                </div>
                <div style={{ width: '50%' }}>
                  <div style={{ backgroundColor: '#F3F4F6', padding: '1rem', borderRadius: '0.5rem' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                      Frequency Ratios:
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                      <span>High-Frequency (B1-B2):</span>
                      <span style={{ fontWeight: '500' }}>0.76</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                      <span>Low-Frequency (B3-B5):</span>
                      <span style={{ fontWeight: '500' }}>0.24</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Statistics */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>Summary Statistics</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ backgroundColor: '#EFF6FF', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>Total IPEs</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>1,667</div>
                </div>
                <div style={{ backgroundColor: '#ECFDF5', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>Transcripts with IPEs</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>283/300</div>
                </div>
                <div style={{ backgroundColor: '#F3E8FF', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>Writing IPE Density</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>1.67</div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>per 100 words</div>
                </div>
                <div style={{ backgroundColor: '#FEF9C3', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>Speaking IPE Density</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>1.73</div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>per 100 words</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Group Analysis Tab */}
        {activeTab === 'groups' && (
  <div style={gridTwoColumnsStyle}>
    {/* Control vs Experimental - Speaking Density */}
    <div style={cardStyle}>
      <h2 style={cardTitleStyle}>Control vs Experimental Group - Speaking Phases IPE Density</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="phase" type="category" allowDuplicatedCategory={false} />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line 
            data={controlGroupData.filter(item => item.phase === 'SP1' || item.phase === 'SP2')} 
            name="Control Group" 
            dataKey="density" 
            stroke="#8884d8" 
          />
          <Line 
            data={experimentalGroupData.filter(item => item.phase === 'SP1' || item.phase === 'SP2')} 
            name="Experimental Group" 
            dataKey="density" 
            stroke="#82ca9d" 
          />
        </LineChart>
      </ResponsiveContainer>
    </div>

    {/* Control vs Experimental - Writing Density */}
    <div style={cardStyle}>
      <h2 style={cardTitleStyle}>Control vs Experimental Group - Writing Phases IPE Density</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="phase" type="category" allowDuplicatedCategory={false} />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line 
            data={controlGroupData.filter(item => item.phase === 'W1' || item.phase === 'W2' || item.phase === 'W3')} 
            name="Control Group" 
            dataKey="density" 
            stroke="#8884d8" 
          />
          <Line 
            data={experimentalGroupData.filter(item => item.phase === 'W1' || item.phase === 'W2' || item.phase === 'W3')} 
            name="Experimental Group" 
            dataKey="density" 
            stroke="#82ca9d" 
          />
        </LineChart>
      </ResponsiveContainer>
    </div>

            {/* Control Group Metrics */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>Control Group Metrics</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={controlGroupData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="phase" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="density" name="IPE Density" fill="#8884d8" />
                  <Bar dataKey="variety" name="IPE Variety" fill="#82ca9d" />
                  <Bar dataKey="highFreqRatio" name="High-Freq Ratio" fill="#ffc658" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Experimental Group Metrics */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>Experimental Group Metrics</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={experimentalGroupData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="phase" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="density" name="IPE Density" fill="#8884d8" />
                  <Bar dataKey="variety" name="IPE Variety" fill="#82ca9d" />
                  <Bar dataKey="highFreqRatio" name="High-Freq Ratio" fill="#ffc658" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Phase-to-Phase Changes */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Phase-to-Phase Changes</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={phaseChangesData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="phase" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="densityChange" name="Density Change (%)" fill="#8884d8" />
                  <Bar dataKey="ratioChange" name="High-Freq Ratio Change" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                <p>Notable changes:</p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', marginTop: '0.5rem' }}>
                  <li>Experimental group showed dramatic density increase (129%) from W1 to W2</li>
                  <li>Control group density decreased 25.8% from W1 to W2, then increased 24.2% from W2 to W3</li>
                  <li>Experimental group showed largest high-frequency ratio drop (-0.11) from SP1 to SP2</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Writing vs Speaking Tab */}
        {activeTab === 'modeComparison' && (
          <div style={gridTwoColumnsStyle}>
            {/* Mode Comparison - Metrics */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>Writing vs Speaking - Key Metrics</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={modeComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mode" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="density" name="IPE Density" fill="#8884d8" />
                  <Bar dataKey="variety" name="IPE Variety" fill="#82ca9d" />
                  <Bar dataKey="highFreqRatio" name="High-Freq Ratio" fill="#ffc658" />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ backgroundColor: '#EFF6FF', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>Writing IPEs</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>735</div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>(43,892 words)</div>
                </div>
                <div style={{ backgroundColor: '#ECFDF5', padding: '1rem', borderRadius: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>Speaking IPEs</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>932</div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>(53,762 words)</div>
                </div>
              </div>
            </div>

            {/* Top Expressions by Mode */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>Top 5 Expressions by Mode</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={topExpressionsPerMode} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="expression" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Frequency" fill="#8884d8">
                    {topExpressionsPerMode.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.mode === 'Writing' ? '#8884d8' : '#82ca9d'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '1rem', height: '1rem', backgroundColor: '#A855F7', borderRadius: '50%', marginRight: '0.5rem' }}></div>
                  <span style={{ fontSize: '0.875rem' }}>Writing</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '1rem', height: '1rem', backgroundColor: '#10B981', borderRadius: '50%', marginRight: '0.5rem' }}></div>
                  <span style={{ fontSize: '0.875rem' }}>Speaking</span>
                </div>
              </div>
            </div>

            {/* Frequency Band Distribution by Mode */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>Frequency Band Distribution by Mode</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={modeComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mode" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="band1" name="Band 1" stackId="a" fill="#8884d8" />
                  <Bar dataKey="band2" name="Band 2" stackId="a" fill="#83a6ed" />
                  <Bar dataKey="band3" name="Band 3" stackId="a" fill="#8dd1e1" />
                  <Bar dataKey="band4" name="Band 4" stackId="a" fill="#82ca9d" />
                  <Bar dataKey="band5" name="Band 5" stackId="a" fill="#a4de6c" />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                <p>Key observations:</p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', marginTop: '0.5rem' }}>
                  <li>Speaking has higher use of Band 1 expressions (564 vs 339)</li>
                  <li>Writing uses slightly more Band 2 expressions (256 vs 225)</li>
                  <li>Both modes have similar high-frequency ratios (Writing: 0.75, Speaking: 0.78)</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* CERF Levels Tab */}
        {activeTab === 'cerfLevels' && (
          // <div style={gridTwoColumnsStyle}>
          //   {/* CERF Level Density Comparison */}
          //   <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
          //     <h2 style={cardTitleStyle}>CERF Level - IPE Density Comparison</h2>
          //     <ResponsiveContainer width="100%" height={300}>
          //       <LineChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          //         <CartesianGrid strokeDasharray="3 3" />
          //         <XAxis dataKey="phase" type="category" allowDuplicatedCategory={false} />
          //         <YAxis />
          //         <Tooltip />
          //         <Legend />
          //         {/* <Line data={cerfA2Data} name="A2 Level" dataKey="density" stroke="#ff6361" /> */}
          //         <Line data={cerfB1Data} name="B1 Level" dataKey="density" stroke="#8884d8" />
          //         <Line data={cerfB2Data} name="B2 Level" dataKey="density" stroke="#82ca9d" />
          //       </LineChart>
          //     </ResponsiveContainer>
          //   </div>
          <div style={gridTwoColumnsStyle}>
  {/* CERF Level Density Comparison - Speaking */}
  <div style={cardStyle}>
    <h2 style={cardTitleStyle}>CERF Level - Speaking Phases IPE Density</h2>
    <ResponsiveContainer width="100%" height={300}>
      <LineChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="phase" type="category" allowDuplicatedCategory={false} />
        <YAxis />
        <Tooltip />
        <Legend />
        <Line 
          data={cerfB1Data.filter(item => item.phase === 'SP1' || item.phase === 'SP2')} 
          name="B1 Level" 
          dataKey="density" 
          stroke="#8884d8" 
        />
        <Line 
          data={cerfB2Data.filter(item => item.phase === 'SP1' || item.phase === 'SP2')} 
          name="B2 Level" 
          dataKey="density" 
          stroke="#82ca9d" 
        />
      </LineChart>
    </ResponsiveContainer>
  </div>

  {/* CERF Level Density Comparison - Writing */}
  <div style={cardStyle}>
    <h2 style={cardTitleStyle}>CERF Level - Writing Phases IPE Density</h2>
    <ResponsiveContainer width="100%" height={300}>
      <LineChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="phase" type="category" allowDuplicatedCategory={false} />
        <YAxis />
        <Tooltip />
        <Legend />
        <Line 
          data={cerfB1Data.filter(item => item.phase === 'W1' || item.phase === 'W2' || item.phase === 'W3')} 
          name="B1 Level" 
          dataKey="density" 
          stroke="#8884d8" 
        />
        <Line 
          data={cerfB2Data.filter(item => item.phase === 'W1' || item.phase === 'W2' || item.phase === 'W3')} 
          name="B2 Level" 
          dataKey="density" 
          stroke="#82ca9d" 
        />
      </LineChart>
    </ResponsiveContainer>
  </div>

            {/* B1 vs B2 Density Difference */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>B1 vs B2 Density Difference</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={b1b2ComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="phase" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="diffDensity" name="Density Difference (B2-B1)" fill="#8884d8">
                    {b1b2ComparisonData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.diffDensity >= 0 ? '#82ca9d' : '#ff6361'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                <p>Key observations:</p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', marginTop: '0.5rem' }}>
                  <li>B2 level generally shows higher density than B1 in most phases</li>
                  <li>Largest positive difference in W3 (0.46)</li>
                  <li>Negative difference in SP2 (-0.21)</li>
                </ul>
              </div>
            </div>

            {/* B1 vs B2 High-Frequency Ratio Difference */}
            <div style={cardStyle}>
              <h2 style={cardTitleStyle}>B1 vs B2 High-Freq Ratio Difference</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={b1b2ComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="phase" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="diffHFR" name="High-Freq Ratio Diff (B2-B1)" fill="#8884d8">
                    {b1b2ComparisonData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.diffHFR >= 0 ? '#82ca9d' : '#ff6361'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                <p>Key observations:</p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', marginTop: '0.5rem' }}>
                  <li>B1 generally has higher high-frequency ratios than B2 (negative differences)</li>
                  <li>SP1 and W1 phases show higher B2 ratios</li>
                  <li>Largest negative difference in SP2 (-0.13)</li>
                </ul>
              </div>
            </div>

            {/* CERF Level Summary */}
            <div style={{ ...cardStyle, gridColumn: 'span 2' }}>
              <h2 style={cardTitleStyle}>CERF Level Summary</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                {/* <div style={{ backgroundColor: '#FEE2E2', padding: '1rem', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>A2 Level</div>
                  <div style={{ fontSize: '0.875rem' }}>Very limited IPE usage</div>
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                      <span>Total IPEs:</span>
                      <span style={{ fontWeight: '500' }}>15</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                      <span>Avg. Density:</span>
                      <span style={{ fontWeight: '500' }}>1.01</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                      <span>Avg. High-Freq Ratio:</span>
                      <span style={{ fontWeight: '500' }}>0.89</span>
                    </div>
                  </div>
                </div> */}
                <div style={{ backgroundColor: '#DBEAFE', padding: '1rem', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>B1 Level</div>
                  <div style={{ fontSize: '0.875rem' }}>Moderate IPE usage</div>
                  <div style={{ marginTop: '0.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
        <span>Total IPEs:</span>
        <span style={{ fontWeight: '500' }}>690</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
        <span>Avg. Density:</span>
        <span style={{ fontWeight: '500' }}>1.55</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
        <span>Avg. High-Freq Ratio:</span>
        <span style={{ fontWeight: '500' }}>0.79</span>
      </div>
                  </div>
                </div>
                <div style={{ backgroundColor: '#DCFCE7', padding: '1rem', borderRadius: '0.5rem' }}>
                  <div style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>B2 Level</div>
                  <div style={{ fontSize: '0.875rem' }}>Advanced IPE usage</div>
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                      <span>Total IPEs:</span>
                      <span style={{ fontWeight: '500' }}>977</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                      <span>Avg. Density:</span>
                      <span style={{ fontWeight: '500' }}>1.73</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                      <span>Avg. High-Freq Ratio:</span>
                      <span style={{ fontWeight: '500' }}>0.75</span>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: '#6B7280' }}>
                <p style={{ fontWeight: 500 }}>Key findings:</p>
                <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', marginTop: '0.5rem' }}>
                  <li>B2 learners use more IPEs overall (higher density) than B1 learners</li>
                  <li>B1 learners tend to rely more on high-frequency expressions than B2 learners</li>
                  <li>B2 learners show more variety in their IPE usage across different phases</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <footer style={footerStyle}>
          <p>IPE Usage Analysis Dashboard | Data from 283 transcripts</p>
        </footer>
      </div>
    </div>
  );
};

export default IPEAnalysisDashboard;
